import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  DescriptionList,
  ErrorState,
  Field,
  FreshnessBadge,
  Inline,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Textarea,
  badgeToneForStatus,
} from "@evolua/ui";
import { EvoluaApiError } from "@evolua/api-client";
import { useAuth } from "../auth/AuthContext.js";
import { buildLoginPath } from "../auth/auth-redirect.js";
import {
  useMeCorrections,
  useMeDataExports,
  useMeProfile,
  useDownloadMeDataExport,
  usePatchMeProfile,
  useRequestMeCorrection,
  useRequestMeDataExport,
  useRevealMeField,
} from "../api/use-me.js";
import { hasPermission } from "../session/demo-session.js";
import { useSession } from "../session/SessionContext.js";
import { createTelemetryDebugError } from "../telemetry/init-telemetry.js";
import { track } from "../telemetry/product-events.js";
import { getStoredPushSubscription, registerPushSubscription } from "../pwa/push.js";

type RevealField = "cpf" | "bankAccount" | "salaryAmount";

function originLabel(
  fieldOrigins: Array<{ field: string; ownership: string }>,
  field: string,
) {
  return fieldOrigins.find((f) => f.field === field)?.ownership ?? "ERP";
}

/** Perfil do colaborador (self-only) — FASE-06-T33. */
function ProfileSection() {
  const query = useMeProfile();
  const patch = usePatchMeProfile();
  const reveal = useRevealMeField();
  const requestCorrection = useRequestMeCorrection();
  const corrections = useMeCorrections();

  const [editing, setEditing] = useState(false);
  const [displayNameDraft, setDisplayNameDraft] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Partial<Record<RevealField, string>>>({});
  const [lastAudit, setLastAudit] = useState<string | null>(null);

  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [correctionField, setCorrectionField] = useState("fullName");
  const [correctionValue, setCorrectionValue] = useState("");
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionError, setCorrectionError] = useState<string | null>(null);

  const detail = query.data;

  useEffect(() => {
    if (!detail || editing) return;
    setDisplayNameDraft(detail.displayName ?? "");
  }, [detail, editing]);

  const correlationId =
    query.error instanceof EvoluaApiError ? query.error.problem.traceId : undefined;

  async function onReveal(field: RevealField) {
    const result = await reveal.mutateAsync(field);
    setRevealed((prev) => ({ ...prev, [field]: String(result.value) }));
    setLastAudit(result.auditId);
  }

  async function onSave() {
    if (!detail) return;
    setEditError(null);
    try {
      const updated = await patch.mutateAsync({
        expectedVersion: detail.version,
        displayName: displayNameDraft.trim() || null,
      });
      setLastAudit(updated.fieldHistory[0]?.auditId ?? null);
      setEditing(false);
    } catch (err) {
      setEditError(
        err instanceof EvoluaApiError
          ? err.problem.detail
          : err instanceof Error
            ? err.message
            : "Falha ao salvar",
      );
    }
  }

  async function onRequestCorrection() {
    setCorrectionError(null);
    if (!correctionValue.trim() || !correctionReason.trim()) {
      setCorrectionError("Informe o novo valor e o motivo da correção");
      return;
    }
    try {
      const currentValue = detail
        ? String((detail as Record<string, unknown>)[correctionField] ?? "")
        : null;
      await requestCorrection.mutateAsync({
        field: correctionField,
        currentValue,
        requestedValue: correctionValue.trim(),
        reason: correctionReason.trim(),
      });
      setCorrectionValue("");
      setCorrectionReason("");
      setShowCorrectionForm(false);
    } catch (err) {
      setCorrectionError(
        err instanceof EvoluaApiError
          ? err.problem.detail
          : err instanceof Error
            ? err.message
            : "Falha ao registrar solicitação",
      );
    }
  }

  if (query.isPending) {
    return <Skeleton height="10rem" label="Carregando perfil" />;
  }

  if (query.isError || !detail) {
    return (
      <ErrorState
        {...(correlationId ? { correlationId } : {})}
        onRetry={() => void query.refetch()}
      />
    );
  }

  return (
    <Card title="Meu perfil" className="evolua-mt">
      <div className="evolua-stack evolua-stack--3">
        <p>
          Dados cadastrais — origem ERP/plataforma/híbrido; revelação e correções
          auditadas.
        </p>
        <FreshnessBadge syncedAt={detail.syncedAt ?? null} />
      </div>

      <DescriptionList
        items={[
          {
            term: "Nome completo",
            aside: (
              <Badge tone="brand">{originLabel(detail.fieldOrigins, "fullName")}</Badge>
            ),
            description: detail.fullName,
          },
          {
            term: "Apelido (plataforma)",
            aside: (
              <Badge tone="neutral">
                {originLabel(detail.fieldOrigins, "displayName")}
              </Badge>
            ),
            description: editing ? (
              <Field
                label="Apelido"
                value={displayNameDraft}
                onChange={(e) => setDisplayNameDraft(e.target.value)}
                maxLength={120}
                aria-label="Apelido"
              />
            ) : (
              (detail.displayName ?? "—")
            ),
          },
          {
            term: "E-mail",
            aside: (
              <Badge tone="warning">{originLabel(detail.fieldOrigins, "email")}</Badge>
            ),
            description: (
              <>
                {detail.email ?? "—"}
                {!detail.emailEditable ? (
                  <span className="evolua-muted">
                    HÍBRIDO já preenchido pelo ERP — solicite correção abaixo
                  </span>
                ) : null}
              </>
            ),
          },
          {
            term: "CPF",
            aside: <Badge tone="brand">{originLabel(detail.fieldOrigins, "cpf")}</Badge>,
            description: (
              <>
                {revealed.cpf ?? detail.cpf ?? "—"}
                {!revealed.cpf ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={reveal.isPending}
                    onClick={() => void onReveal("cpf")}
                  >
                    Revelar
                  </Button>
                ) : null}
              </>
            ),
          },
          {
            term: "Dados bancários",
            aside: (
              <Badge tone="brand">
                {originLabel(detail.fieldOrigins, "bankAccount")}
              </Badge>
            ),
            description: (
              <>
                {revealed.bankAccount ?? detail.bankAccount ?? "—"}
                {!revealed.bankAccount ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={reveal.isPending}
                    onClick={() => void onReveal("bankAccount")}
                  >
                    Revelar
                  </Button>
                ) : null}
              </>
            ),
          },
        ]}
      />

      <Inline>
        {editing ? (
          <>
            <Button
              type="button"
              size="sm"
              disabled={patch.isPending}
              onClick={() => void onSave()}
            >
              Salvar
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={patch.isPending}
              onClick={() => {
                setEditing(false);
                setEditError(null);
                setDisplayNameDraft(detail.displayName ?? "");
              }}
            >
              Cancelar
            </Button>
          </>
        ) : (
          <Button type="button" size="sm" onClick={() => setEditing(true)}>
            Editar apelido
          </Button>
        )}
        <span className="evolua-muted">versão {detail.version}</span>
      </Inline>

      {editError ? <Alert tone="danger">{editError}</Alert> : null}

      {lastAudit ? (
        <p className="evolua-muted">Última ação auditada · {lastAudit}</p>
      ) : null}

      <div className="evolua-mt">
        {showCorrectionForm ? (
          <form
            className="evolua-stack evolua-stack--3"
            onSubmit={(e) => {
              e.preventDefault();
              void onRequestCorrection();
            }}
          >
            <Select
              label="Campo"
              value={correctionField}
              onChange={(e) => setCorrectionField(e.target.value)}
              options={[
                { value: "fullName", label: "Nome completo" },
                { value: "cpf", label: "CPF" },
                { value: "registrationNumber", label: "Matrícula" },
                { value: "departmentName", label: "Área" },
                { value: "positionName", label: "Cargo" },
              ]}
            />
            <Field
              label="Novo valor"
              value={correctionValue}
              onChange={(e) => setCorrectionValue(e.target.value)}
              maxLength={500}
            />
            <Textarea
              label="Motivo"
              value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)}
              rows={2}
              maxLength={1000}
            />
            {correctionError ? <Alert tone="danger">{correctionError}</Alert> : null}
            <div className="evolua-stack evolua-stack--3">
              <Button type="submit" size="sm" disabled={requestCorrection.isPending}>
                Enviar solicitação
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setShowCorrectionForm(false)}
              >
                Cancelar
              </Button>
            </div>
          </form>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setShowCorrectionForm(true)}
          >
            Solicitar correção de dado ERP
          </Button>
        )}
      </div>

      {corrections.data && corrections.data.data.length > 0 ? (
        <div className="evolua-mt">
          <h3 className="evolua-section-title">Solicitações de correção</h3>
          <Table
            caption="Correções"
            getRowId={(c) => c.id}
            columns={[
              { id: "field", header: "Campo", cell: (c) => c.field },
              { id: "from", header: "Atual", cell: (c) => c.currentValue ?? "—" },
              { id: "to", header: "Solicitado", cell: (c) => c.requestedValue },
              {
                id: "status",
                header: "Status",
                cell: (c) => (
                  <Badge tone={badgeToneForStatus(c.status)}>{c.status}</Badge>
                ),
              },
            ]}
            rows={corrections.data.data}
          />
        </div>
      ) : null}
    </Card>
  );
}

function LgpdExportSection() {
  const list = useMeDataExports();
  const request = useRequestMeDataExport();
  const download = useDownloadMeDataExport();
  const [error, setError] = useState<string | null>(null);
  const [lastAudit, setLastAudit] = useState<string | null>(null);

  async function onRequest() {
    setError(null);
    try {
      await request.mutateAsync();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao solicitar exportação");
    }
  }

  async function onDownload(id: string) {
    setError(null);
    try {
      const result = await download.mutateAsync(id);
      const blob = new Blob([result.content], { type: "application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
      setLastAudit(result.auditId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao baixar exportação");
    }
  }

  return (
    <Card title="Portabilidade (LGPD)" className="evolua-mt">
      <p>
        Solicite um arquivo JSON com seus dados pessoais (inventário da plataforma). O
        download tem validade curta e é auditado.
      </p>
      <Button
        type="button"
        size="sm"
        disabled={request.isPending}
        onClick={() => void onRequest()}
      >
        {request.isPending ? "Gerando…" : "Solicitar exportação"}
      </Button>
      {list.isPending ? (
        <Skeleton height="3rem" label="Exportações" />
      ) : list.data && list.data.data.length > 0 ? (
        <Table
          caption="Exportações"
          className="evolua-mt"
          getRowId={(job) => job.id}
          columns={[
            {
              id: "status",
              header: "Status",
              cell: (job) => (
                <Badge tone={badgeToneForStatus(job.status)}>{job.status}</Badge>
              ),
            },
            {
              id: "when",
              header: "Solicitado",
              cell: (job) => new Date(job.requestedAt).toLocaleString("pt-BR"),
            },
            {
              id: "action",
              header: "Arquivo",
              cell: (job) =>
                job.status === "READY" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={download.isPending}
                    onClick={() => void onDownload(job.id)}
                  >
                    Baixar JSON
                  </Button>
                ) : (
                  "—"
                ),
            },
          ]}
          rows={list.data.data}
        />
      ) : (
        <p>Nenhuma exportação ainda.</p>
      )}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {lastAudit ? <p className="evolua-muted">Download auditado · {lastAudit}</p> : null}
    </Card>
  );
}

function PushSettingsSection() {
  const [pushStatus, setPushStatus] = useState<string | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const vapidConfigured = Boolean(import.meta.env.VITE_VAPID_PUBLIC_KEY);

  useEffect(() => {
    if (getStoredPushSubscription()) {
      setPushStatus("Assinatura Web Push já registrada neste dispositivo.");
    } else if (!vapidConfigured) {
      setPushStatus("VITE_VAPID_PUBLIC_KEY não configurada neste ambiente.");
    }
  }, [vapidConfigured]);

  return (
    <>
      <p>
        Receba avisos no dispositivo quando o app estiver instalado. Requer{" "}
        <code>VITE_VAPID_PUBLIC_KEY</code> e permissão do navegador.
      </p>
      {pushStatus ? <p role="status">{pushStatus}</p> : null}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        loading={pushBusy}
        disabled={!vapidConfigured}
        onClick={() => {
          setPushBusy(true);
          setPushStatus(null);
          void registerPushSubscription()
            .then(() => {
              setPushStatus("Assinatura de push registrada com sucesso.");
            })
            .catch((err: unknown) => {
              setPushStatus(
                err instanceof Error ? err.message : "Falha ao registrar push",
              );
            })
            .finally(() => setPushBusy(false));
        }}
      >
        Registrar Web Push
      </Button>
    </>
  );
}

export function AccountPage() {
  const { session, isDemoMode } = useSession();
  const auth = useAuth();
  const navigate = useNavigate();
  const [crash, setCrash] = useState(false);
  const [confirmGlobalLogout, setConfirmGlobalLogout] = useState(false);

  const canSeeProfile = useMemo(
    () => Boolean(session && hasPermission(session, ["lgpd:export_own"])),
    [session],
  );

  if (crash) {
    throw createTelemetryDebugError();
  }

  if (!session) return null;

  async function onLogout(global: boolean) {
    await auth.logout({ global, reason: "logout" });
    navigate(buildLoginPath({ reason: "logout" }), { replace: true });
  }

  return (
    <>
      <PageHeader
        title="Conta"
        lead={
          isDemoMode
            ? "Sessão demo local (VITE_AUTH_MODE=demo)."
            : "Dados derivados do id token Cognito."
        }
      />
      <Card title={session.displayName}>
        <p>E-mail: {session.email}</p>
        <p>
          Perfil: <Badge tone="neutral">{session.role}</Badge>
        </p>
        {!isDemoMode ? (
          <div className="evolua-stack evolua-stack--3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void onLogout(false)}
            >
              Sair desta sessão
            </Button>
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={() => setConfirmGlobalLogout(true)}
            >
              Sair de todos os dispositivos
            </Button>
          </div>
        ) : null}
      </Card>

      {canSeeProfile ? <ProfileSection /> : null}
      {canSeeProfile ? <LgpdExportSection /> : null}

      <Card title="Notificações push" className="evolua-mt">
        <PushSettingsSection />
      </Card>

      {import.meta.env.DEV || import.meta.env.VITE_TELEMETRY_DEBUG === "1" ? (
        <Card title="Telemetria (debug)" className="evolua-mt">
          <p>
            Provoca um erro de interface para validar ErrorBoundary + Sentry/console
            (FASE-05-T15).
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              track("telemetry.debug_error");
              setCrash(true);
            }}
          >
            Provocar erro de UI
          </Button>
        </Card>
      ) : null}

      <Modal
        open={confirmGlobalLogout}
        title="Sair de todos os dispositivos"
        onClose={() => setConfirmGlobalLogout(false)}
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirmGlobalLogout(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => {
                setConfirmGlobalLogout(false);
                void onLogout(true);
              }}
            >
              Encerrar todas as sessões
            </Button>
          </>
        }
      >
        <p>
          Isso encerra a sessão neste navegador e nos demais dispositivos autenticados.
        </p>
      </Modal>
    </>
  );
}
